import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';

/**
 * Line Items + Sub-Items editor for a Customer Contract.
 *
 * Props:
 *   contractId   — required
 *   contractStart, contractEnd — defaults for line-item dates
 *   onDirty      — callback when user makes unsaved changes (unused today — saves are inline)
 */
export default function ContractLineItemsEditor({ contractId, contractStart, contractEnd }) {
  const [lineItems, setLineItems] = useState([]);
  const [products, setProducts]   = useState([]);
  const [modules, setModules]     = useState([]);   // = "Skill Groups" — { id, product_id, name }
  const [skillsets, setSkillsets] = useState([]);   // = "Skill Sets" — { id, module_id, product_ids[], name }
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState({}); // {lineItemId: true}
  const [subItemsByLine, setSubItemsByLine] = useState({}); // {lineItemId: [rows]}
  const [savingSubFor, setSavingSubFor] = useState(null);

  const load = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    try {
      const [liRes, metaRes, empRes] = await Promise.all([
        apiFetch(`/api/contracts/${contractId}/line-items`),
        apiFetch('/api/metadata/cascade'),
        apiFetch('/api/employees?limit=500'),
      ]);
      if (liRes.ok) { const d = await liRes.json(); setLineItems(d.lineItems || []); }
      if (metaRes.ok) {
        const d = await metaRes.json();
        setProducts((d.products || []).map(p => ({ value: p.id, label: p.name })));
        // Keep raw module / skillset objects so we can filter by product_id / module_id later.
        setModules(d.modules || []);
        setSkillsets(d.skillsets || []);
      }
      if (empRes.ok) { const d = await empRes.json(); setEmployees(d.employees || []); }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [contractId]);

  useEffect(() => { load(); }, [load]);

  /* ── line item actions ── */
  async function addLineItem() {
    try {
      const res = await apiFetch(`/api/contracts/${contractId}/line-items`, {
        method: 'POST',
        body: JSON.stringify({
          seq: (lineItems.length + 1) * 10,
          itemName: 'New line item',
          startDate: contractStart || null,
          endDate: contractEnd || null,
          rate: 0, amount: 0, effortDays: 0,
        }),
      });
      if (res.ok) { await load(); toast.success('Line item added'); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.error || 'Failed to add'); }
    } catch (e) { toast.error(e.message); }
  }

  async function saveLineItem(li, patch) {
    // Optimistic local update
    setLineItems(rows => rows.map(r => r.id === li.id ? { ...r, ...patch } : r));
    try {
      const body = {
        seq: patch.seq, itemName: patch.item_name,
        productId: patch.product_id, serviceDescription: patch.service_description,
        startDate: patch.start_date, endDate: patch.end_date,
        rate: patch.rate, amount: patch.amount, effortDays: patch.effort_days,
        engagementModel: patch.engagement_model, notes: patch.notes,
      };
      // Strip undefined
      Object.keys(body).forEach(k => body[k] === undefined && delete body[k]);
      const res = await apiFetch(`/api/contracts/${contractId}/line-items/${li.id}`, {
        method: 'PUT', body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); toast.error(`Save failed: ${d.error || ''}`); await load(); }
    } catch (e) { toast.error(`Network error: ${e.message}`); await load(); }
  }

  async function deleteLineItem(li) {
    const ok = await toast.confirm({
      title: 'Delete line item?',
      body: `Line item "${li.item_name || li.service_description || li.id}" and all its sub-items will be removed.`,
      danger: true, confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      const res = await apiFetch(`/api/contracts/${contractId}/line-items/${li.id}`, { method: 'DELETE' });
      if (res.ok) { await load(); toast.success('Line item deleted'); }
    } catch (e) { toast.error(e.message); }
  }

  /* ── sub-item load/save ── */
  async function loadSubsFor(lineItemId) {
    try {
      const res = await apiFetch(`/api/contracts/${contractId}/line-items/${lineItemId}/sub-items`);
      if (res.ok) {
        const d = await res.json();
        setSubItemsByLine(s => ({ ...s, [lineItemId]: d.subItems || d.sub_items || [] }));
      }
    } catch (_) { /* ignore */ }
  }

  function toggleLine(lineItemId) {
    const next = !expanded[lineItemId];
    setExpanded(e => ({ ...e, [lineItemId]: next }));
    if (next && !subItemsByLine[lineItemId]) loadSubsFor(lineItemId);
  }

  function addSubItemLocal(lineItemId) {
    const existing = subItemsByLine[lineItemId] || [];
    const li = lineItems.find(x => x.id === lineItemId);
    const row = {
      _local: true,
      id: '_tmp_' + Date.now(),
      seq: (existing.length + 1) * 10,
      sub_item_name: 'New sub-item',
      skillset_id: null, skillset_name: '',
      start_date: li?.start_date || contractStart || null,
      end_date: li?.end_date || contractEnd || null,
      quantity: 1,
      time_uom: 'Man-Month',
      allocation_pct: 100, rate: null, amount: null,
      engagement_model: 'Offshore Named', notes: '',
    };
    setSubItemsByLine(s => ({ ...s, [lineItemId]: [...existing, row] }));
  }

  function patchSubItemLocal(lineItemId, subId, patch) {
    setSubItemsByLine(s => ({
      ...s,
      [lineItemId]: (s[lineItemId] || []).map(r => r.id === subId ? { ...r, ...patch } : r),
    }));
  }

  function removeSubItemLocal(lineItemId, subId) {
    setSubItemsByLine(s => ({
      ...s,
      [lineItemId]: (s[lineItemId] || []).filter(r => r.id !== subId),
    }));
  }

  async function saveSubItems(lineItemId) {
    setSavingSubFor(lineItemId);
    try {
      const rows = (subItemsByLine[lineItemId] || []).map(r => ({
        ...r,
        id: String(r.id || '').startsWith('_tmp_') ? undefined : r.id,
      }));
      const res = await apiFetch(`/api/contracts/${contractId}/line-items/${lineItemId}/sub-items`, {
        method: 'POST',
        body: JSON.stringify(rows),
      });
      if (res.ok) { toast.success('Sub-items saved'); await loadSubsFor(lineItemId); }
      else { const d = await res.json().catch(() => ({})); toast.error(`Save failed: ${d.error || ''}`); }
    } catch (e) { toast.error(`Network error: ${e.message}`); }
    setSavingSubFor(null);
  }

  const productLookup = useMemo(() => Object.fromEntries(products.map(p => [p.value, p.label])), [products]);

  if (!contractId) {
    return (
      <div style={S.emptyBox}>
        Save the contract first to start adding line items and sub-items.
      </div>
    );
  }

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#E5E7EB', letterSpacing: '0.4px' }}>
          Line Items {lineItems.length > 0 && <span style={{ color: '#64748B', fontWeight: 500 }}>· {lineItems.length}</span>}
        </div>
        <button onClick={addLineItem} style={S.addBtn}>+ Add Line Item</button>
      </div>

      {loading ? (
        <div style={{ padding: 14, color: '#94A3B8', fontSize: 12 }}>Loading…</div>
      ) : lineItems.length === 0 ? (
        <div style={S.emptyBox}>
          No line items yet. Click <strong>+ Add Line Item</strong> to create the first one.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Column header — widths line up with the inputs in S.lineTop below */}
          <div style={S.colHeader}>
            <span style={{ width: 22 }}></span>{/* expand */}
            <span style={{ width: 54 }}>Seq</span>
            <span style={{ flex: 2, textAlign: 'left' }}>Name</span>
            <span style={{ flex: 1, textAlign: 'left' }}>Product</span>
            <span style={{ width: 130 }}>Start</span>
            <span style={{ width: 130 }}>End</span>
            <span style={{ width: 70, textAlign: 'right' }}>Days</span>
            <span style={{ width: 90, textAlign: 'right' }}>Rate</span>
            <span style={{ width: 110, textAlign: 'right' }}>Amount</span>
            <span style={{ width: 28 }}></span>{/* delete */}
          </div>
          {lineItems.map(li => (
            <div key={li.id} style={S.lineRow}>
              <div style={S.lineTop}>
                <button onClick={() => toggleLine(li.id)} style={S.expandBtn}>
                  {expanded[li.id] ? '▼' : '▶'}
                </button>
                <input
                  type="number" style={{ ...S.inp, width: 54 }}
                  value={li.seq ?? ''} placeholder="Seq"
                  onChange={ev => saveLineItem(li, { seq: parseInt(ev.target.value, 10) || 0 })}
                />
                <input
                  style={{ ...S.inp, flex: 2 }}
                  value={li.item_name || ''} placeholder="Item name"
                  onChange={ev => setLineItems(rows => rows.map(r => r.id === li.id ? { ...r, item_name: ev.target.value } : r))}
                  onBlur={ev => saveLineItem(li, { item_name: ev.target.value })}
                />
                <select
                  style={{ ...S.inp, flex: 1 }}
                  value={li.product_id || ''}
                  onChange={ev => {
                    const newProduct = ev.target.value || null;
                    saveLineItem(li, { product_id: newProduct });
                    // When the product changes, previously-picked Skill Sets on this
                    // line item's sub-items are invalid (skillset cascades from product).
                    // Clear them locally so the user re-picks a consistent value.
                    if (subItemsByLine[li.id]?.length) {
                      setSubItemsByLine(s => ({
                        ...s,
                        [li.id]: s[li.id].map(r => ({
                          ...r,
                          skillset_id: null, skillset_name: '',
                        })),
                      }));
                    }
                  }}
                >
                  <option value="">Product…</option>
                  {products.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <input
                  type="date" style={{ ...S.inp, width: 130 }}
                  value={li.start_date ? String(li.start_date).slice(0, 10) : ''}
                  onChange={ev => saveLineItem(li, { start_date: ev.target.value || null })}
                />
                <input
                  type="date" style={{ ...S.inp, width: 130 }}
                  value={li.end_date ? String(li.end_date).slice(0, 10) : ''}
                  onChange={ev => saveLineItem(li, { end_date: ev.target.value || null })}
                />
                <input
                  type="number" style={{ ...S.inp, width: 70 }}
                  value={li.effort_days ?? ''} placeholder="Days"
                  onChange={ev => setLineItems(rows => rows.map(r => r.id === li.id ? { ...r, effort_days: ev.target.value } : r))}
                  onBlur={ev => saveLineItem(li, { effort_days: parseFloat(ev.target.value) || 0 })}
                />
                <input
                  type="number" style={{ ...S.inp, width: 90 }}
                  value={li.rate ?? ''} placeholder="Rate"
                  onChange={ev => setLineItems(rows => rows.map(r => r.id === li.id ? { ...r, rate: ev.target.value } : r))}
                  onBlur={ev => saveLineItem(li, { rate: parseFloat(ev.target.value) || 0 })}
                />
                <input
                  type="number" style={{ ...S.inp, width: 110 }}
                  value={li.amount ?? ''} placeholder="Amount"
                  onChange={ev => setLineItems(rows => rows.map(r => r.id === li.id ? { ...r, amount: ev.target.value } : r))}
                  onBlur={ev => saveLineItem(li, { amount: parseFloat(ev.target.value) || 0 })}
                />
                <button onClick={() => deleteLineItem(li)} style={S.delBtn} title="Delete line item">×</button>
              </div>

              {expanded[li.id] && (
                <div style={S.subWrap}>
                  <div style={S.subHeader}>
                    <span style={{ color: '#94A3B8', fontSize: 11, letterSpacing: '0.3px' }}>Sub-items (skillset → employee)</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => addSubItemLocal(li.id)} style={S.addMiniBtn}>+ Add Sub-Item</button>
                      <button
                        onClick={() => saveSubItems(li.id)}
                        disabled={savingSubFor === li.id}
                        style={{ ...S.saveMiniBtn, opacity: savingSubFor === li.id ? 0.6 : 1 }}
                      >{savingSubFor === li.id ? 'Saving…' : 'Save Sub-Items'}</button>
                    </div>
                  </div>
                  {(subItemsByLine[li.id] || []).length === 0 ? (
                    <div style={{ padding: 10, color: '#64748B', fontSize: 11.5 }}>
                      No sub-items. Use <strong>+ Add Sub-Item</strong> to assign a skillset, employee, and dates.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {(subItemsByLine[li.id] || []).map(si => (
                        <div key={si.id} style={S.subRow}>
                          <input
                            type="number" style={{ ...S.inp, width: 48 }}
                            value={si.seq ?? ''} placeholder="#"
                            onChange={ev => patchSubItemLocal(li.id, si.id, { seq: parseInt(ev.target.value, 10) || 0 })}
                          />
                          <input
                            style={{ ...S.inp, flex: 1.5 }}
                            value={si.sub_item_name || ''} placeholder="Sub-item name"
                            onChange={ev => patchSubItemLocal(li.id, si.id, { sub_item_name: ev.target.value })}
                          />
                          {(() => {
                            // Skill Set options = skillsets whose product_ids array contains the parent line item's product_id.
                            const setOpts = li.product_id
                              ? skillsets.filter(s => Array.isArray(s.product_ids) && s.product_ids.includes(li.product_id))
                              : [];
                            const setDisabled = !li.product_id || setOpts.length === 0;
                            return (
                              <select
                                style={{ ...S.inp, flex: 1, opacity: setDisabled ? 0.55 : 1 }}
                                value={si.skillset_id || ''}
                                disabled={setDisabled}
                                title={setDisabled ? 'Pick a Product on the line item first' : 'Skill Set'}
                                onChange={ev => {
                                  const ss = skillsets.find(s => s.id === ev.target.value);
                                  patchSubItemLocal(li.id, si.id, {
                                    skillset_id: ev.target.value || null,
                                    skillset_name: ss ? ss.name : '',
                                  });
                                }}
                              >
                                <option value="">{setDisabled ? '— pick product —' : 'Skill Set…'}</option>
                                {setOpts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                              </select>
                            );
                          })()}
                          <input
                            type="number" step="0.01" min="0"
                            style={{ ...S.inp, width: 70 }}
                            value={si.quantity ?? ''} placeholder="Qty"
                            onChange={ev => patchSubItemLocal(li.id, si.id, { quantity: ev.target.value === '' ? null : parseFloat(ev.target.value) })}
                            title="Quantity (e.g. 6 in 6 Man-Months)"
                          />
                          <select
                            style={{ ...S.inp, width: 120 }}
                            value={si.time_uom || 'Man-Month'}
                            onChange={ev => patchSubItemLocal(li.id, si.id, { time_uom: ev.target.value })}
                            title="Time unit of measure"
                          >
                            <option value="Man-Month">Man-Month</option>
                            <option value="Man-Week">Man-Week</option>
                            <option value="Man-Day">Man-Day</option>
                            <option value="Man-Hour">Man-Hour</option>
                          </select>
                          <input
                            type="date" style={{ ...S.inp, width: 125 }}
                            value={si.start_date ? String(si.start_date).slice(0, 10) : ''}
                            onChange={ev => patchSubItemLocal(li.id, si.id, { start_date: ev.target.value || null })}
                          />
                          <input
                            type="date" style={{ ...S.inp, width: 125 }}
                            value={si.end_date ? String(si.end_date).slice(0, 10) : ''}
                            onChange={ev => patchSubItemLocal(li.id, si.id, { end_date: ev.target.value || null })}
                          />
                          <input
                            type="number" style={{ ...S.inp, width: 60 }}
                            value={si.allocation_pct ?? ''} placeholder="%"
                            onChange={ev => patchSubItemLocal(li.id, si.id, { allocation_pct: parseFloat(ev.target.value) || 0 })}
                          />
                          <button onClick={() => removeSubItemLocal(li.id, si.id)} style={S.delBtn} title="Remove sub-item">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── styles ── */
const S = {
  root: { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 2px' },
  addBtn: {
    padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
    border: '1px solid rgba(0,224,255,0.35)', background: 'rgba(0,224,255,0.08)', color: '#00E0FF', cursor: 'pointer',
  },
  emptyBox: {
    padding: 18, textAlign: 'center', color: '#94A3B8',
    border: '1px dashed rgba(255,255,255,0.10)', borderRadius: 8, fontSize: 12.5,
  },
  lineRow: {
    border: '1px solid rgba(0,224,255,0.20)',
    borderRadius: 8, padding: 10, background: 'rgba(0,224,255,0.04)',
  },
  lineTop: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' },
  colHeader: {
    display: 'flex', gap: 6, alignItems: 'center',
    padding: '6px 10px',
    fontSize: 10.5, fontWeight: 700, letterSpacing: '0.5px',
    textTransform: 'uppercase', color: '#94A3B8',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 6,
  },
  expandBtn: {
    width: 22, height: 26, padding: 0, border: '1px solid rgba(255,255,255,0.10)',
    background: 'transparent', color: '#00E0FF', fontSize: 10, cursor: 'pointer', borderRadius: 4,
  },
  inp: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 5, padding: '6px 8px',
    fontSize: 12, color: '#E5E7EB', outline: 'none', minWidth: 70,
  },
  delBtn: {
    width: 26, height: 26, padding: 0, borderRadius: 5,
    border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.10)',
    color: '#FCA5A5', cursor: 'pointer', fontSize: 14, lineHeight: 1,
  },
  subWrap: {
    marginTop: 8, padding: 10, borderRadius: 7,
    background: 'rgba(191,95,255,0.04)', border: '1px dashed rgba(191,95,255,0.25)',
  },
  subHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  subRow: { display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' },
  addMiniBtn: {
    padding: '4px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
    border: '1px solid rgba(191,95,255,0.35)', background: 'rgba(191,95,255,0.10)',
    color: '#BF5FFF', cursor: 'pointer',
  },
  saveMiniBtn: {
    padding: '4px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
    border: '1px solid rgba(16,185,129,0.35)', background: 'rgba(16,185,129,0.10)',
    color: '#10B981', cursor: 'pointer',
  },
};
